// @flow

/**
 * Module dependencies.
 */

import { Redirect, Route, Router, Switch } from 'react-router-dom';
import { chain } from 'lodash';
import { containers } from 'client/containers';
import { getLocalizedRoutes, googleAnalytics } from 'client/core/utils';
import { getPathname } from 'core/utils';
import { legalPage } from 'client/containers/legal-and-compliance';
import React, { Component } from 'react';
import StickyNavbarProvider from 'client/components/core/sticky-navbar/sticky-navbar-provider';
import createBrowserHistory from 'history/createBrowserHistory';
import entities from 'client/core/entities';
import i18n from 'client/translations/i18n';

/**
 * Containers.
 */

const {
  About,
  App,
  Compliance,
  Contacts,
  ForAgents,
  Home,
  HowItWorks,
  LicensedRepresentatives,
  NotFound
} = containers;

/**
 * `Root` component.
 */

class Root extends Component {

  /**
   * Render route.
   */

  renderRoute = ({ component, language, path }: Object) => {
    return (
      <Route
        component={component}
        exact
        key={getPathname(path, language)}
        path={getPathname(path, language)}
      />
    );
  };

  /**
   * Render routes.
   */

  renderRoutes = (): Array<React$Element<*>> => {
    const { languages } = entities();

    const routes = [{
      component: Home,
      pageKey: 'home'
    }, {
      component: About,
      pageKey: 'about'
    }, {
      component: Compliance,
      pageKey: 'compliance'
    }, {
      component: Contacts,
      pageKey: 'contacts'
    }, {
      component: ForAgents,
      pageKey: 'forAgents'
    }, {
      component: HowItWorks,
      pageKey: 'howItWorks'
    }, {
      component: LicensedRepresentatives,
      pageKey: 'licensedRepresentatives'
    }, {
      component: legalPage('privacy'),
      pageKey: 'privacy'
    }, {
      component: legalPage('termsOfService'),
      pageKey: 'termsOfService'
    }, {
      component: legalPage('unitransferTermsOfService'),
      pageKey: 'unitransferTermsOfService'
    }];

    return chain(languages)
      .map(({ value }) => getLocalizedRoutes(routes, value))
      .flatten()
      .map(this.renderRoute)
      .value();
  }

  /**
   * Render router.
   */

  renderRouter = ({ location }: Object): React$Element<*> => {
    return (
      <App location={location}>
        <Switch
          key={location.key}
          location={location}
        >
          <Redirect
            exact
            from={'/'}
            to={`/${i18n.language}`}
          />

          {this.renderRoutes()}

          <Route
            component={NotFound}
            path={'*'}
          />
        </Switch>
      </App>
    );
  }

  /**
   * Component did mount.
   */

  componentDidMount() {
    googleAnalytics.initialize();
  }

  /**
   * Render.
   */

  render(): React$Element<*> {
    return (
      <StickyNavbarProvider>
        <Router history={createBrowserHistory()}>
          <Route render={this.renderRouter} />
        </Router>
      </StickyNavbarProvider>
    );
  }

}

/**
 * Export `Root` component.
 */

export default Root;



// WEBPACK FOOTER //
// ../src/client/containers/root/index.js